import DOMPurify from 'dompurify';
import { jsPDF } from 'jspdf';

const PRINT_WINDOW_FEATURES =
  'left=0,top=0,width=800,height=600,toolbar=0,scrollbars=0,status=0';

export type PdfSection = {
  dataUrl: string;
  startsNewPage?: boolean;
};

const ILLEGAL_FILENAME_CHARS = /[<>:"/\\|?*]/g;

export const sanitizePdfFilename = (
  filename: string,
  fallback = 'download',
) => {
  const stripExtension = (value: string) => value.replace(/\.pdf$/i, '');
  const sanitize = (value: string) =>
    stripExtension(value)
      .replace(ILLEGAL_FILENAME_CHARS, '')
      .split('')
      .filter((char) => char.charCodeAt(0) > 31)
      .join('')
      .trim();
  const name = sanitize(filename) || sanitize(fallback) || 'download';
  return `${name}.pdf`;
};

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

export const downloadSectionsAsPdf = async (
  sections: PdfSection[],
  filename: string,
) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  const maxY = pageHeight - margin;
  const sectionGap = 4;

  let y = margin;

  for (const section of sections) {
    if (section.startsNewPage) {
      doc.addPage();
      y = margin;
    }

    const img = await loadImage(section.dataUrl);
    const contentHeight = (img.naturalHeight / img.naturalWidth) * contentWidth;

    if (y + contentHeight > maxY) {
      doc.addPage();
      y = margin;
    }

    doc.addImage(
      section.dataUrl,
      'PNG',
      margin,
      y,
      contentWidth,
      contentHeight,
    );

    y += contentHeight + sectionGap;
  }

  doc.save(sanitizePdfFilename(filename));
};

export const downloadHtmlAsPdf = (html: string) => {
  let safeHTML: any = `<div style="width:1060px;font-family:Arial,sans-serif;">${html}</div>`;
  if (window.trustedTypes && window.trustedTypes.createPolicy) {
    const escapedHTML = window.trustedTypes.createPolicy('htmlPdf', {
      createHTML: (string) =>
        DOMPurify.sanitize(string, { RETURN_TRUSTED_TYPE: true }).toString(),
    });
    safeHTML = escapedHTML.createHTML(safeHTML);
  }

  const winPrint = window.open('blank', '', PRINT_WINDOW_FEATURES);
  winPrint?.document.write(safeHTML);
  winPrint?.document.close();
  winPrint?.focus();
  winPrint?.print();
  winPrint?.close();
};
